import { Link } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import { useMemo, useState, useEffect } from 'react';
import { useFinanceRecords } from '../../store/financeStore';
import AnnualChart from './components/AnnualChart';
import QualityTrendCard from './components/QualityTrendCard';
import InventoryDetailModal from './components/InventoryDetailModal';

export default function Dashboard() {
  const { records: financeRecords } = useFinanceRecords();
  const [showInventoryDetail, setShowInventoryDetail] = useState(false);

  const today = new Date().toLocaleDateString('zh-CN', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    weekday: 'long'
  });

  // 报送任务倒计时
  const submissionTasks = useMemo(() => {
    const now = new Date();
    const tasks = [
      {
        id: 1,
        title: '2026年1月 月度报送',
        deadline: new Date('2026-02-15'),
        type: '月度报送',
        department: '省级粮储部门',
      },
      {
        id: 2,
        title: '2026年Q1 季度报送',
        deadline: new Date('2026-04-15'),
        type: '季度报送',
        department: '省级粮储部门',
      },
      {
        id: 3,
        title: '2025年度 年度报送',
        deadline: new Date('2026-03-31'),
        type: '年度报送',
        department: '省级粮储部门',
      },
    ];
    return tasks.map((t) => {
      const diff = t.deadline.getTime() - now.getTime();
      const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return { ...t, daysLeft };
    }).sort((a, b) => a.daysLeft - b.daysLeft);
  }, []);

  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getCountdownColor = (days: number) => {
    if (days <= 3) return { bg: 'bg-rose-500', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-200', ring: 'from-rose-500 to-rose-600' };
    if (days <= 7) return { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200', ring: 'from-amber-500 to-amber-600' };
    if (days <= 15) return { bg: 'bg-orange-400', text: 'text-orange-600', light: 'bg-orange-50', border: 'border-orange-200', ring: 'from-orange-400 to-orange-500' };
    return { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', ring: 'from-emerald-500 to-emerald-600' };
  };

  const nearestTask = submissionTasks[0];
  const nearestColor = getCountdownColor(nearestTask.daysLeft);

  // 计算最近任务的时分秒倒计时
  const getTimeLeft = () => {
    const diff = nearestTask.deadline.getTime() - currentTime.getTime();
    if (diff <= 0) return { d: 0, h: 0, m: 0 };
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { d, h, m };
  };
  const timeLeft = getTimeLeft();

  const stats = [
    { 
      label: '今日收购', 
      value: '1,250', 
      unit: '吨', 
      icon: 'ri-arrow-down-circle-line',
      color: 'from-[#0c7362] to-[#085b4d]',
      bgColor: 'bg-[#0c7362]/10',
      textColor: 'text-[#0c7362]'
    },
    { 
      label: '今日销售', 
      value: '980', 
      unit: '吨', 
      icon: 'ri-arrow-up-circle-line',
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    },
    { 
      label: '总库存', 
      value: '15,680', 
      unit: '吨', 
      icon: 'ri-database-2-line',
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    },
    { 
      label: '待处理记录', 
      value: '8', 
      unit: '条', 
      icon: 'ri-file-list-line',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    }
  ];

  const inventory = [
    { variety: '小麦', quantity: 5680, capacity: 8000, unit: '吨', trend: '+5.2%' },
    { variety: '玉米', quantity: 4200, capacity: 6000, unit: '吨', trend: '+3.8%' },
    { variety: '大米', quantity: 3800, capacity: 5000, unit: '吨', trend: '-2.1%' },
    { variety: '大豆', quantity: 2000, capacity: 3000, unit: '吨', trend: '+1.5%' }
  ];

  const activities = [
    { time: '10:30', type: '收购', content: '从张三收购小麦 50吨', status: 'success' },
    { time: '09:15', type: '销售', content: '向ABC粮油销售玉米 80吨', status: 'info' },
    { time: '08:45', type: '入库', content: '大米入库 120吨', status: 'success' },
    { time: '昨天', type: '报表', content: '生成月度购销存报表', status: 'warning' }
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 ml-64">
        <div className="p-8">
          {/* 欢迎区域 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">欢迎回来，管理员</h1>
            <p className="text-gray-500 flex items-center">
              <i className="ri-calendar-line mr-2"></i>
              {today}
            </p>
          </div>

          {/* 报送任务倒计时提醒卡片 */}
          <div className={`mb-8 rounded-2xl border ${nearestColor.border} overflow-hidden`}>
            <div className={`${nearestColor.light} p-6`}>
              <div className="flex items-start justify-between">
                {/* 左侧：最近任务倒计时 */}
                <div className="flex items-center space-x-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${nearestColor.ring} rounded-2xl flex items-center justify-center shadow-lg`}>
                    <i className="ri-alarm-warning-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-800">报送任务提醒</h3>
                      <span className={`text-xs font-medium ${nearestColor.text} ${nearestColor.light} px-3 py-1 rounded-full border ${nearestColor.border}`}>
                        {nearestTask.daysLeft <= 3 ? '紧急' : nearestTask.daysLeft <= 7 ? '即将到期' : '进行中'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      <span className="font-medium text-gray-800">{nearestTask.title}</span>
                      <span className="mx-2 text-gray-300">|</span>
                      截止：{nearestTask.deadline.toLocaleDateString('zh-CN')}
                      <span className="mx-2 text-gray-300">|</span>
                      {nearestTask.department}
                    </p>
                    {/* 倒计时数字 */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1.5">
                        <div className={`bg-gradient-to-br ${nearestColor.ring} text-white rounded-xl px-3.5 py-2 min-w-[52px] text-center shadow-md`}>
                          <div className="text-2xl font-bold leading-none">{timeLeft.d}</div>
                          <div className="text-[10px] opacity-80 mt-0.5">天</div>
                        </div>
                        <span className={`text-xl font-bold ${nearestColor.text}`}>:</span>
                        <div className={`bg-gradient-to-br ${nearestColor.ring} text-white rounded-xl px-3.5 py-2 min-w-[52px] text-center shadow-md`}>
                          <div className="text-2xl font-bold leading-none">{timeLeft.h}</div>
                          <div className="text-[10px] opacity-80 mt-0.5">时</div>
                        </div>
                        <span className={`text-xl font-bold ${nearestColor.text}`}>:</span>
                        <div className={`bg-gradient-to-br ${nearestColor.ring} text-white rounded-xl px-3.5 py-2 min-w-[52px] text-center shadow-md`}>
                          <div className="text-2xl font-bold leading-none">{timeLeft.m}</div>
                          <div className="text-[10px] opacity-80 mt-0.5">分</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 右侧：操作按钮 + 其他任务 */}
                <div className="flex flex-col items-end space-y-3">
                  <Link
                    to="/submission"
                    className="px-6 py-2.5 bg-gradient-to-r from-[#0c7362] to-[#085b4d] text-white rounded-xl hover:shadow-lg transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line mr-2"></i>前往报送
                  </Link>
                  <div className="text-right">
                    {submissionTasks.slice(1).map((task) => {
                      const c = getCountdownColor(task.daysLeft);
                      return (
                        <div key={task.id} className="flex items-center space-x-2 mt-1.5">
                          <span className={`w-2 h-2 rounded-full ${c.bg}`}></span>
                          <span className="text-xs text-gray-500">{task.title}</span>
                          <span className={`text-xs font-semibold ${c.text}`}>
                            {task.daysLeft}天
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 统计卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 card-hover cursor-pointer"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                    <i className={`${stat.icon} text-2xl ${stat.textColor}`}></i>
                  </div>
                  <div className={`px-3 py-1 bg-gradient-to-r ${stat.color} text-white text-xs rounded-full font-medium`}>
                    实时
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-sm">{stat.label}</p>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-800">{stat.value}</span>
                    <span className="text-gray-500 text-sm ml-2">{stat.unit}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 年度经营数据可视化图表（新增） */}
          <div className="mb-8">
            <AnnualChart records={financeRecords} year="2026" />
          </div>

          {/* 质量合格率趋势监控 */}
          <div className="mb-8">
            <QualityTrendCard />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 库存概览 */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-soft">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <i className="ri-pie-chart-line mr-2 text-[#0c7362]"></i>
                  库存概览
                </h2>
                <button
                  onClick={() => setShowInventoryDetail(true)}
                  className="text-sm text-[#0c7362] hover:text-[#085b4d] font-medium whitespace-nowrap cursor-pointer"
                >
                  查看详情 <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              <div className="space-y-4">
                {inventory.map((item, index) => {
                  const percentage = (item.quantity / item.capacity) * 100;
                  const isLow = percentage < 50;
                  const isMedium = percentage >= 50 && percentage < 80;
                  
                  return (
                    <div key={index} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-800">{item.variety}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item.trend.startsWith('+') ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {item.trend}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">
                          {item.quantity} / {item.capacity} {item.unit}
                        </span>
                      </div>
                      <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`absolute left-0 top-0 h-full rounded-full transition-all duration-500 ${
                            isLow ? 'bg-gradient-to-r from-red-400 to-red-500' :
                            isMedium ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                            'bg-gradient-to-r from-green-400 to-green-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 text-right">
                        {percentage.toFixed(1)}% 容量
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 业务动态 */}
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <i className="ri-time-line mr-2 text-[#0c7362]"></i>
                业务动态
              </h2>
              <div className="space-y-4 relative">
                <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#0c7362] to-gray-200"></div>
                {activities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 relative">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                      activity.status === 'success' ? 'bg-green-100' :
                      activity.status === 'info' ? 'bg-[#0c7362]/10' :
                      'bg-orange-100'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        activity.status === 'success' ? 'bg-green-500' :
                        activity.status === 'info' ? 'bg-[#0c7362]' :
                        'bg-orange-500'
                      }`}></div>
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          activity.status === 'success' ? 'bg-green-100 text-green-700' :
                          activity.status === 'info' ? 'bg-[#0c7362]/10 text-[#0c7362]' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {activity.type}
                        </span>
                        <span className="text-xs text-gray-400">{activity.time}</span>
                      </div>
                      <p className="text-sm text-gray-600">{activity.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 库存批次明细弹窗 */}
          <InventoryDetailModal open={showInventoryDetail} onClose={() => setShowInventoryDetail(false)} />
        </div>
      </div>
    </div>
  );
}
